// The blocking playback protocol (spec-v4 section 8.2, V4-K1). A Director tool with `mode: "wait"` (play_and_wait)
// returns a `play` directive with `wait: true`; src/director.ts runTool doesn't answer the agent until playback here
// settles with one of the fixed outcomes below. Pure: the clock, the timers and the "still here" ping are injected,
// so playwait.test.ts (and the mock-Director e2e in product/demo) drive it without a browser.
//
//   ended     the item played to its end (or to the end of its range)
//   stopped   the user stopped it, or the call ended while it played
//   blocked   the browser refused to play without a tap (the tap button stays up; after the tap and the end the
//             client sends an "[app] finished …" user message instead, since this tool call is already answered)
//   replaced  something else started playing
//   timeout   the guard ran out (durationS + 20 s), or the element failed: never hang the call
//   (paused is not an outcome: the wait carries on, and the guard stops counting until it resumes)
//
// While a wait is pending: `sendUserActivity()` every 15 s (so a turn timeout can't nudge the Director into talking
// over the video), and the job updates session.ts would send as contextual updates are queued until it settles.

export type WaitOutcome = "ended" | "stopped" | "blocked" | "replaced" | "timeout";
export type WaitWhat = "clip" | "render" | "audio";

/** The client guard: a wait always settles within the item's length plus this. */
export const GUARD_EXTRA_S = 20;
/** The guard when the directive gave no length (the longest item that blocks, see LONG_ITEM_S). */
export const DEFAULT_DURATION_S = 120;
/** "The user is still here" while waiting (spike V4-03 section 5). */
export const ACTIVITY_MS = 15_000;
/** Decided (spec-v4 brief): items longer than this use the non-blocking `play`; Director minutes bill during a wait. */
export const LONG_ITEM_S = 120;

/** Whether a play_and_wait directive should block (a known length over two minutes doesn't). */
export const blocks = (durationS: number | null | undefined) => !(typeof durationS === "number" && durationS > LONG_ITEM_S);

const NOUN: Record<WaitWhat, string> = { clip: "the clip", render: "the video", audio: "the audio" };

/** The fixed outcome strings the Director gets as the tool result. */
export function outcomeText(o: WaitOutcome, info: { seconds: number; title?: string | null; what?: WaitWhat | null }): string {
  const n = Math.max(0, Math.round(info.seconds));
  switch (o) {
    case "ended":
      return `It finished playing (${n} s): ${info.title?.trim() || NOUN[info.what ?? "clip"]}. Ask what they think in a few words.`;
    case "stopped":
      return `The user stopped it after ${n} s.`;
    case "blocked":
      return "The browser blocked playback. Say 'tap to play' and nothing more.";
    case "replaced":
      return "Something else started playing.";
    case "timeout":
      return "Playback didn't finish; carry on.";
  }
}

export type WaitInfo = { token: string; durationS?: number | null; title?: string | null; what?: WaitWhat | null };

type Timer = unknown;
export type PlayWaitDeps = {
  now: () => number;
  setTimer: (f: () => void, ms: number) => Timer;
  clearTimer: (t: Timer) => void;
  /** conversation.sendUserActivity() */
  activity: () => void;
  activityMs?: number;
};

type Pending = {
  info: WaitInfo;
  resolve: (text: string) => void;
  began: number;
  /** when `playing` first fired (seconds are counted from here) */
  playingAt: number | null;
  /** seconds played before a pause */
  playedBefore: number;
  paused: boolean;
  deadline: number;
  guardLeft: number;
  guard: Timer | null;
  ping: Timer | null;
};

export type Settled = { token: string; outcome: WaitOutcome; text: string };

/** At most one pending wait: a new one settles the old as `replaced`. */
export class PlayWaits {
  private p: Pending | null = null;
  private listeners: ((s: Settled) => void)[] = [];
  constructor(private d: PlayWaitDeps) {}

  /** The pending wait's token, or null. */
  pending(): string | null {
    return this.p?.info.token ?? null;
  }

  /** Start waiting for `info.token`; resolves with the outcome text the agent hears. */
  begin(info: WaitInfo): Promise<string> {
    if (this.p) this.settle(this.p.info.token, "replaced");
    return new Promise<string>((resolve) => {
      const now = this.d.now();
      const guardMs = ((info.durationS && info.durationS > 0 ? info.durationS : DEFAULT_DURATION_S) + GUARD_EXTRA_S) * 1000;
      const p: Pending = { info, resolve, began: now, playingAt: null, playedBefore: 0, paused: false, deadline: now + guardMs, guardLeft: guardMs, guard: null, ping: null };
      this.p = p;
      this.arm(p, guardMs);
      const tick = () => {
        if (this.p !== p) return;
        this.d.activity();
        p.ping = this.d.setTimer(tick, this.d.activityMs ?? ACTIVITY_MS);
      };
      p.ping = this.d.setTimer(tick, this.d.activityMs ?? ACTIVITY_MS);
    });
  }

  private arm(p: Pending, ms: number) {
    if (p.guard !== null) this.d.clearTimer(p.guard);
    p.deadline = this.d.now() + ms;
    p.guard = this.d.setTimer(() => {
      if (this.p === p) this.settle(p.info.token, "timeout");
    }, ms);
  }

  private mine(token: string | null | undefined): Pending | null {
    return this.p && token && this.p.info.token === token ? this.p : null;
  }

  /** The element fired `playing` for this token (first start, or a resume after a pause). */
  playing(token: string | null | undefined) {
    const p = this.mine(token);
    if (!p) return;
    if (p.playingAt === null) p.playingAt = this.d.now();
    if (p.paused) {
      p.paused = false;
      p.playingAt = this.d.now();
      this.arm(p, p.guardLeft);
    }
  }

  /** Paused (a Pause tap, the `pause` tool): keep waiting, but stop the guard counting down. */
  pause(token: string | null | undefined) {
    const p = this.mine(token);
    if (!p || p.paused) return;
    p.paused = true;
    if (p.playingAt !== null) p.playedBefore += (this.d.now() - p.playingAt) / 1000;
    p.playingAt = null;
    p.guardLeft = Math.max(1000, p.deadline - this.d.now());
    if (p.guard !== null) this.d.clearTimer(p.guard);
    p.guard = null;
  }

  resume(token: string | null | undefined) {
    this.playing(token);
  }

  /** Seconds played so far for the pending wait. */
  played(): number {
    const p = this.p;
    if (!p) return 0;
    return p.playedBefore + (p.playingAt !== null ? (this.d.now() - p.playingAt) / 1000 : 0);
  }

  /**
   * Settle the wait for `token` (a stale token is ignored and returns false). `seconds` overrides the clock's count
   * (the player knows the element's own position).
   */
  settle(token: string | null | undefined, outcome: WaitOutcome, seconds?: number): boolean {
    const p = this.mine(token);
    if (!p) return false;
    const played = this.played();
    this.p = null;
    if (p.guard !== null) this.d.clearTimer(p.guard);
    if (p.ping !== null) this.d.clearTimer(p.ping);
    const secs = seconds ?? (outcome === "ended" && p.info.durationS ? p.info.durationS : played);
    const text = outcomeText(outcome, { seconds: secs, title: p.info.title, what: p.info.what });
    p.resolve(text);
    const s: Settled = { token: p.info.token, outcome, text };
    for (const f of [...this.listeners]) f(s);
    return true;
  }

  /** The call ended (any reason) mid-wait: nothing can be sent any more; resolve as `stopped`. Playback carries on. */
  callEnded(): boolean {
    return this.p ? this.settle(this.p.info.token, "stopped") : false;
  }

  /** Called after every settle (session.ts flushes its queue; the player drops a timed-out element). */
  onSettle(f: (s: Settled) => void): () => void {
    this.listeners.push(f);
    return () => {
      this.listeners = this.listeners.filter((x) => x !== f);
    };
  }
}

// ------------------------------------------------------------------ job updates held while waiting

/**
 * session.ts's job updates: sent at once, or held while a play_and_wait is pending (the Director must not announce
 * a finished frame over a clip) and delivered in order after it settles. Keeps the newest `max`.
 */
export function contextQueue(waiting: () => boolean, send: (text: string) => void, max = 12) {
  const held: string[] = [];
  return {
    push(text: string) {
      if (!waiting()) return send(text);
      held.push(text);
      if (held.length > max) held.splice(0, held.length - max);
    },
    flush() {
      if (waiting()) return;
      for (const t of held.splice(0)) send(t);
    },
    size: () => held.length,
  };
}

// ------------------------------------------------------------------ "[app]" user messages (finding F3)

/** How long an [app] user message waits for the Director to stop talking before it goes as a contextual update. */
export const APP_HOLD_MAX_MS = 10_000;
/** The Director must have been quiet this long (a pause between sentences isn't the end of its turn). */
export const APP_QUIET_MS = 400;

/**
 * An `[app]` user message makes the Director answer (contextual updates promise no reply), but sent while it talks
 * it would cut it off. So:
 *   hold     a play_and_wait is pending (the tool result speaks first), or the Director is talking / just stopped
 *   send     it's quiet: send the user message
 *   context  it kept talking past APP_HOLD_MAX_MS: fall back to a contextual update rather than interrupt
 */
export function appMessageAction(s: { waiting: boolean; speaking: boolean; quietMs: number; heldMs: number }): "hold" | "send" | "context" {
  if (s.waiting) return "hold";
  if (s.speaking || s.quietMs < APP_QUIET_MS) return s.heldMs >= APP_HOLD_MAX_MS ? "context" : "hold";
  return "send";
}

// ------------------------------------------------------------------ runTool: does this tool's ui block?

type PlayLike = { type: "play"; wait?: boolean; token?: string; durationS?: number; title?: string; what?: WaitWhat; then?: string };
const isWaitPlay = (o: unknown): o is PlayLike => !!o && typeof o === "object" && (o as { type?: unknown }).type === "play" && (o as { wait?: unknown }).wait === true;

/**
 * Read a tool's `ui` for play_and_wait's directive (`{type: "play", wait: true}`, alone or in a list):
 *   wait    the wait to begin (its token: the directive's, or a fresh one written back into the directive)
 *   ui      the directive to apply. A known length over LONG_ITEM_S drops `wait` (the non-blocking `play`, which
 *           asks with an "[app] finished …" user message at the end) and runTool answers at once.
 * Only the first waiting play blocks; any later one in the same ui plays without waiting.
 */
export function planWait<U>(ui: U, makeToken: () => string): { ui: U; wait: WaitInfo | null } {
  let wait: WaitInfo | null = null;
  let seen = false;
  const fix = (o: unknown): unknown => {
    if (!isWaitPlay(o)) return o;
    if (seen || !blocks(o.durationS)) return { ...o, wait: false, then: o.then ?? "ask" };
    seen = true;
    const token = o.token || makeToken();
    wait = { token, durationS: o.durationS ?? null, title: o.title ?? null, what: o.what ?? null };
    return { ...o, token };
  };
  const out = (Array.isArray(ui) ? ui.map(fix) : fix(ui)) as U;
  return { ui: out, wait };
}
